import { BaseClient } from '../client/BaseClient';
import {
  Group,
  CreateGroupRequest,
  UpdateGroupRequest,
} from '../types';

export class Groups {
  constructor(private client: BaseClient) {}

  /**
   * List all groups
   * @param tel - Phone number
   */
  async list(tel: string): Promise<Group[]> {
    const response = await this.client['get']<{
      success: boolean;
      groups: Group[];
    }>(`/api/groups/${encodeURIComponent(tel)}`);
    
    return response.groups;
  }

  /**
   * Get group details
   * @param tel - Phone number
   * @param groupId - Group ID
   */
  async get(tel: string, groupId: string): Promise<Group> {
    const response = await this.client['get']<{
      success: boolean;
      group: Group;
    }>(`/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}`);
    
    return response.group;
  }

  /**
   * Create a new group
   * @param data - Group creation data
   */
  async create(data: CreateGroupRequest): Promise<Group> {
    const response = await this.client['post']<{
      success: boolean;
      group: Group;
    }>('/api/groups', data);
    
    return response.group;
  }

  /**
   * Update group details
   * @param tel - Phone number
   * @param groupId - Group ID
   * @param data - Update data
   */
  async update(
    tel: string,
    groupId: string,
    data: UpdateGroupRequest
  ): Promise<{ success: boolean }> {
    return this.client['put']<{ success: boolean }>(
      `/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}`,
      data
    );
  }

  /**
   * Leave a group
   * @param tel - Phone number
   * @param groupId - Group ID
   */
  async leave(tel: string, groupId: string): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(
      `/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}/leave`
    );
  }

  /**
   * Add participants to a group
   * @param tel - Phone number
   * @param groupId - Group ID
   * @param participants - Array of participant IDs
   */
  async addParticipants(
    tel: string,
    groupId: string,
    participants: string[]
  ): Promise<{ success: boolean; added: string[]; failed: string[] }> {
    return this.client['post']<{
      success: boolean;
      added: string[];
      failed: string[];
    }>(`/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}/participants`, {
      participants,
    });
  }

  /**
   * Remove participants from a group
   * @param tel - Phone number
   * @param groupId - Group ID
   * @param participants - Array of participant IDs
   */
  async removeParticipants(
    tel: string,
    groupId: string,
    participants: string[]
  ): Promise<{ success: boolean }> {
    return this.client['delete']<{ success: boolean }>(
      `/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}/participants`,
      { data: { participants } }
    );
  }

  /**
   * Promote participants to admin
   * @param tel - Phone number
   * @param groupId - Group ID
   * @param participants - Array of participant IDs
   */
  async promoteParticipants(
    tel: string,
    groupId: string,
    participants: string[]
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(
      `/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}/promote`,
      { participants }
    );
  }

  /**
   * Demote participants from admin
   * @param tel - Phone number
   * @param groupId - Group ID
   * @param participants - Array of participant IDs
   */
  async demoteParticipants(
    tel: string,
    groupId: string,
    participants: string[]
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(
      `/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}/demote`,
      { participants }
    );
  }

  /**
   * Get group invite link
   * @param tel - Phone number
   * @param groupId - Group ID
   */
  async getInviteLink(
    tel: string,
    groupId: string
  ): Promise<{ code: string; url: string }> {
    const response = await this.client['get']<{
      success: boolean;
      code: string;
      url: string;
    }>(`/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}/invite`);
    
    return { code: response.code, url: response.url };
  }

  /**
   * Revoke group invite link
   * @param tel - Phone number
   * @param groupId - Group ID
   */
  async revokeInviteLink(
    tel: string,
    groupId: string
  ): Promise<{ code: string; url: string }> {
    const response = await this.client['post']<{
      success: boolean;
      code: string;
      url: string;
    }>(`/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}/invite/revoke`);
    
    return { code: response.code, url: response.url };
  }

  /**
   * Accept group invite
   * @param tel - Phone number
   * @param inviteCode - Invite code
   */
  async acceptInvite(
    tel: string,
    inviteCode: string
  ): Promise<{ groupId: string }> {
    const response = await this.client['post']<{
      success: boolean;
      groupId: string;
    }>('/api/groups/join', { tel, inviteCode });
    
    return { groupId: response.groupId };
  }

  /**
   * Set group profile picture
   * @param tel - Phone number
   * @param groupId - Group ID
   * @param image - Base64 encoded image
   */
  async setProfilePicture(
    tel: string,
    groupId: string,
    image: string
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(
      `/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}/picture`,
      { image }
    );
  }

  /**
   * Remove group profile picture
   * @param tel - Phone number
   * @param groupId - Group ID
   */
  async removeProfilePicture(
    tel: string,
    groupId: string
  ): Promise<{ success: boolean }> {
    return this.client['delete']<{ success: boolean }>(
      `/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}/picture`
    );
  }

  /**
   * Get group members who have read a message
   * @param tel - Phone number
   * @param groupId - Group ID
   * @param messageId - Message ID
   */
  async getMessageReaders(
    tel: string,
    groupId: string,
    messageId: string
  ): Promise<string[]> {
    const response = await this.client['get']<{
      success: boolean;
      readers: string[];
    }>(`/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}/readers/${messageId}`);
    
    return response.readers;
  }

  /**
   * Toggle group settings
   * @param tel - Phone number
   * @param groupId - Group ID
   * @param setting - Setting to toggle
   * @param value - New value
   */
  async toggleSetting(
    tel: string,
    groupId: string,
    setting: 'announce' | 'restrict' | 'locked',
    value: boolean
  ): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(
      `/api/groups/${encodeURIComponent(tel)}/${encodeURIComponent(groupId)}/settings`,
      { [setting]: value }
    );
  }
}